/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/system';
import { getClient } from '@sentry/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  useAuthenticationContext,
  useHasConnectionIssue,
} from '@opengeoweb/authentication';
import { SentryRecordButton } from '@opengeoweb/sentry';
import type { ConfigType } from '@opengeoweb/shared';
import { AppHeader } from '@opengeoweb/shared';
import { routerActions } from '@opengeoweb/store';
import { OverlayStyle, ContainerStyle } from '@opengeoweb/theme';
import {
  WORKSPACE_NAMESPACE,
  workspaceActions,
  workspaceRoutes,
} from '@opengeoweb/workspace';
import Menu from './Menu';
import { ConnectionIssueBanner } from '../../components/ConnectionIssueBanner';
import UserMenu from '../../components/UserMenu';

interface HeaderProps {
  config?: ConfigType;
  workspaceTopBar?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({
  config,
  workspaceTopBar,
}: HeaderProps) => {
  const [recordButtonEnabled, setRecordButtonEnabled] = React.useState(false);
  const sentryInitialized = !!getClient();

  const { auth, onSetAuth, authConfig } = useAuthenticationContext();
  // Re-renders when has_connection_issue flips, for ConnectionIssueBanner.
  useHasConnectionIssue();
  const dispatch = useDispatch();
  const { t: workspaceT } = useTranslation(WORKSPACE_NAMESPACE);

  const fetchWorkspace = React.useCallback(
    (newWorkspaceId: string): void => {
      dispatch(
        workspaceActions.fetchWorkspace({ workspaceId: newWorkspaceId }),
      );
    },
    [dispatch],
  );

  const changeToEmptyMapWorkspace = React.useCallback((): void => {
    dispatch(
      workspaceActions.changeToEmptyMapWorkspace({
        newMapPresetText: workspaceT('workspace-mappreset-new'),
        newWorkspaceText: workspaceT('workspace-new'),
      }),
    );
  }, [dispatch, workspaceT]);

  const navigateToUrl = React.useCallback((): void => {
    dispatch(routerActions.navigateToUrl({ url: workspaceRoutes.root }));
  }, [dispatch]);

  const navigateToEmptyWorkspace = (): void => {
    changeToEmptyMapWorkspace();
    navigateToUrl();
  };

  const workspaceId = config?.GW_INITIAL_WORKSPACE_PRESET;

  const overlayStyle = OverlayStyle();
  const containerStyle = ContainerStyle();

  return (
    <>
      <Box sx={{ ...overlayStyle }} />
      <AppHeader
        menu={<Menu config={config} />}
        onClick={(): void => {
          workspaceId
            ? fetchWorkspace(workspaceId)
            : navigateToEmptyWorkspace();
        }}
        title={config?.GW_FEATURE_APP_TITLE ?? 'GeoWeb'}
        userMenu={
          authConfig ? (
            <>
              <ConnectionIssueBanner
                auth={auth!}
                authConfig={authConfig}
                onSetAuth={onSetAuth}
              />
              {sentryInitialized && recordButtonEnabled ? (
                <SentryRecordButton />
              ) : null}
              <UserMenu
                config={config}
                isAuthConfigured={true}
                recordButtonEnabled={recordButtonEnabled}
                sentryInitialized={sentryInitialized}
                setRecordButtonEnabled={setRecordButtonEnabled}
                userName={auth?.username ?? 'Guest'}
                userRoles={auth?.roles}
              />
            </>
          ) : (
            <UserMenu userName="Guest" />
          )
        }
        workspaceTopBar={workspaceTopBar}
      />
      <Box sx={{ ...containerStyle }} />
    </>
  );
};

export default Header;
